import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { InvoiceResource, InvoicesMemberResource } from '@/types/resources';
import { api } from '@/utils/api';
import { PaymentMethod } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

import InvoicesBankField from './InvoicesBankField';
import InvoicesReceiverType from './InvoicesReceiverType';
import InvoicesRecieverField from './InvoicesRecieverField';

type FormDataT = {
  receiver: string;
  bank_account_id: number;
  payment_method: PaymentMethod;
  // Never added to defaultValues or submitted — this screen doesn't send the
  // wallet emails, but handleFormErrors<FormDataT> needs the keys so a
  // server-side error keyed on them types cleanly.
  paypal_email?: string;
  payoneer_email?: string;
};

// Kept mounted at all times and hidden via CSS rather than removed from the
// tree when a wallet is selected, so InvoicesBankField's mount effect (which
// clears bank_account_id if the receiver changed from the invoice's original
// member) doesn't re-fire and wipe a just-picked bank account on toggle.
const HideableLarger = styled(Larger)<{ $hidden?: boolean }>`
  ${({ $hidden }) =>
    $hidden &&
    css`
      display: none;
    `}
`;

interface Props {
  onClose: () => void;
  invoice: InvoiceResource;
}

const InvoicesChangeReceiverModal: FC<Props> = ({ onClose, invoice }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  // The member matched by InvoicesRecieverField against the currently typed
  // receiver name. null until the suggestion list resolves, and null again
  // whenever the typed name matches nobody; only used once the receiver no
  // longer equals the invoice's own member name (see activeMember below).
  const [matchedMember, setMatchedMember] =
    useState<InvoicesMemberResource | null>(null);

  const { data: initialMember } = useQuery<InvoicesMemberResource>(
    ['invoices_members', { id: invoice.member_id }],
    () =>
      api.get<never, InvoicesMemberResource>(
        `/v1/payments/members/${invoice.member_id}`,
      ),
    { enabled: !!invoice.member_id },
  );

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      receiver: invoice.member?.name ?? '',
      bank_account_id: invoice.bank_account_id ?? undefined,
      payment_method: invoice.payment_method ?? PaymentMethod.Bank,
    },
  });

  const receiver = useWatch({ control, name: 'receiver' });
  const paymentMethod = useWatch({ control, name: 'payment_method' });
  const isWallet = paymentMethod !== PaymentMethod.Bank;

  const activeMember =
    receiver === invoice.member?.name ? initialMember : matchedMember;
  const paypalEmail = activeMember?.paypal_email;
  const payoneerEmail = activeMember?.payoneer_email;

  const disabledMethods = [
    ...(paypalEmail ? [] : [PaymentMethod.Paypal]),
    ...(payoneerEmail ? [] : [PaymentMethod.Payoneer]),
  ];

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        // A wallet invoice has no bank account; leave bank_account_id in form
        // state so switching back to bank transfer restores the original one.
        const { bank_account_id, ...withoutBank } = data;
        const payload =
          data.payment_method === PaymentMethod.Bank ? data : withoutBank;

        await api.put(`/v1/payments/invoices/${invoice.id}/receiver`, payload);

        queryClient.invalidateQueries(['invoices_members']);
        queryClient.invalidateQueries(['invoices_list'], { exact: false });

        onClose();
        return alerts.success('Receiver has been updated successfully');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="Change Receiver & Type" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <InvoicesRecieverField
                  control={control}
                  name="receiver"
                  onMemberMatch={setMatchedMember}
                />
                {!!errors.receiver && (
                  <ValidationMessage>
                    {errors.receiver.message}
                  </ValidationMessage>
                )}
              </Larger>
              <Larger>
                <InvoicesReceiverType
                  paymentMethod={paymentMethod}
                  setValue={setValue}
                  disabledMethods={disabledMethods}
                />
              </Larger>
              {isWallet && (
                <Larger>
                  <Label htmlFor="wallet_email">Receiver Email</Label>
                  <Input
                    id="wallet_email"
                    value={
                      (paymentMethod === PaymentMethod.Payoneer
                        ? payoneerEmail
                        : paypalEmail) ?? ''
                    }
                    disabled
                    readOnly
                  />
                  {!!(errors.paypal_email || errors.payoneer_email) && (
                    <ValidationMessage>
                      {errors.paypal_email?.message ??
                        errors.payoneer_email?.message}
                    </ValidationMessage>
                  )}
                </Larger>
              )}
              <HideableLarger $hidden={isWallet}>
                <InvoicesBankField
                  name="bank_account_id"
                  control={control}
                  setValue={setValue}
                  initialBankId={invoice.bank_account_id}
                  initialReceiver={invoice?.member.name ?? ''}
                />
              </HideableLarger>
              {!!errors.bank_account_id && (
                <ValidationMessage>
                  {errors.bank_account_id.message}
                </ValidationMessage>
              )}
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Save
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default InvoicesChangeReceiverModal;
